import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getAllSofaModels, createSofaModel, updateSofaModel, deleteSofaModel, reorderSofaModels } from '../api';
import { can } from '../capabilities';

const DEFAULT_CATEGORY = 'Без категории';

function readFolderPreferences(key) {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return {
      order: Array.isArray(saved?.order) ? saved.order.filter(category => typeof category === 'string') : [],
      expanded: saved?.expanded && typeof saved.expanded === 'object' && !Array.isArray(saved.expanded)
        ? saved.expanded : {},
    };
  } catch {
    return { order: [], expanded: {} };
  }
}

export default function ModelsManagement({ user }) {
  const canDelete = can(user, 'deleteModel');
  const [models, setModels] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentModel, setCurrentModel] = useState(null);
  const preferencesKey = `model_folder_preferences:${user?.id ?? 'default'}`;
  const [folderPreferences, setFolderPreferences] = useState(() => readFolderPreferences(preferencesKey));
  const [isReordering, setIsReordering] = useState(false);
  const [reorderError, setReorderError] = useState('');

  useEffect(() => {
    setFolderPreferences(readFolderPreferences(preferencesKey));
  }, [preferencesKey]);

  const saveFolderPreferences = (changes) => {
    const next = { ...folderPreferences, ...changes };
    setFolderPreferences(next);
    try { localStorage.setItem(preferencesKey, JSON.stringify(next)); } catch {}
  };

  const groupedModels = new Map();
  models.forEach(model => {
    const category = model.category || DEFAULT_CATEGORY;
    if (!groupedModels.has(category)) groupedModels.set(category, []);
    groupedModels.get(category).push(model);
  });
  const savedCategoryOrder = [...new Set(folderPreferences.order)].filter(category => groupedModels.has(category));
  const newCategories = [...groupedModels.keys()].filter(category => !savedCategoryOrder.includes(category)).sort((a, b) => {
    if (a === DEFAULT_CATEGORY) return 1;
    if (b === DEFAULT_CATEGORY) return -1;
    return a.localeCompare(b);
  });
  const categories = [...savedCategoryOrder, ...newCategories];

  const moveFolder = (sourceIndex, destinationIndex) => {
    if (sourceIndex === destinationIndex || destinationIndex < 0 || destinationIndex >= categories.length) return;
    const nextOrder = [...categories];
    const [category] = nextOrder.splice(sourceIndex, 1);
    nextOrder.splice(destinationIndex, 0, category);
    saveFolderPreferences({ order: nextOrder });
  };
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    fabric_consumption: '',
    fabric_consumption_2: '',
    frame_rate: '',
    sewing_rate: '',
    upholstery_rate: '',
    material_cost: '',
  });

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      const response = await getAllSofaModels();
      setModels(response.data);
    } catch (error) {
      console.error('Ошибка загрузки моделей:', error);
    }
  };

  const handleOpenAddDialog = () => {
    setEditMode(false);
    setFormData({
      name: '',
      category: '',
      description: '',
      fabric_consumption: '',
      fabric_consumption_2: '',
      frame_rate: '',
      sewing_rate: '',
      upholstery_rate: '',
      material_cost: '',
    });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (model) => {
    setEditMode(true);
    setCurrentModel(model);
    setFormData({
      name: model.name || '',
      category: model.category || '',
      description: model.description || '',
      fabric_consumption: model.fabric_consumption || '',
      fabric_consumption_2: model.fabric_consumption_2 || '',
      frame_rate: model.frame_rate || '',
      sewing_rate: model.sewing_rate || '',
      upholstery_rate: model.upholstery_rate || '',
      material_cost: model.material_cost || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentModel(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Валидация
    if (!formData.name) {
      alert('Пожалуйста, укажите название модели');
      return;
    }

    try {
      const dataToSend = {
        name: formData.name,
        category: formData.category || null,
        description: formData.description || null,
        fabric_consumption: formData.fabric_consumption !== '' ? parseFloat(formData.fabric_consumption) : 0,
        fabric_consumption_2: formData.fabric_consumption_2 !== '' ? parseFloat(formData.fabric_consumption_2) : 0,
        frame_rate: formData.frame_rate !== '' ? parseFloat(formData.frame_rate) : 0,
        sewing_rate: formData.sewing_rate !== '' ? parseFloat(formData.sewing_rate) : 0,
        upholstery_rate: formData.upholstery_rate !== '' ? parseFloat(formData.upholstery_rate) : 0,
        material_cost: formData.material_cost !== '' ? parseFloat(formData.material_cost) : 0,
      };
      if (editMode) {
        await updateSofaModel(currentModel.id, dataToSend);
        // Оптимистично патчим локально — без перезагрузки
        setModels(prev => prev.map(m => m.id === currentModel.id ? { ...m, ...dataToSend } : m));
      } else {
        const created = await createSofaModel(dataToSend);
        if (created?.data) {
          setModels(prev => [...prev, created.data]);
        } else {
          await loadModels();
        }
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Ошибка сохранения модели:', error);
      const errorMsg = error.response?.data?.detail || 'Неизвестная ошибка';
      alert(`Ошибка: ${errorMsg}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить модель? Это может повлиять на существующие диваны.')) {
      // Оптимистично убираем
      setModels(prev => prev.filter(m => m.id !== id));
      try {
        await deleteSofaModel(id);
      } catch (error) {
        console.error('Ошибка удаления модели:', error);
        alert('Ошибка удаления модели');
        await loadModels();
      }
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;
    if (result.type === 'MODEL_CATEGORY') {
      moveFolder(sourceIndex, destIndex);
      return;
    }
    if (isReordering || result.source.droppableId !== result.destination.droppableId) return;

    const category = result.source.droppableId.replace('category-', '');
    const categoryModels = [...(groupedModels.get(category) || [])];
    const [moved] = categoryModels.splice(sourceIndex, 1);
    if (!moved) return;
    categoryModels.splice(destIndex, 0, moved);
    const newModels = categories.flatMap(cat => cat === category ? categoryModels : groupedModels.get(cat));
    const previousModels = models;
    setModels(newModels);
    setIsReordering(true);
    setReorderError('');

    try {
      await reorderSofaModels(newModels.map(m => m.id));
    } catch (error) {
      console.error('Ошибка перемещения:', error);
      setModels(previousModels);
      setReorderError('Не удалось сохранить порядок моделей. Попробуйте ещё раз.');
    } finally {
      setIsReordering(false);
    }
  };

  const calculateTotalCost = (model) => {
    const workCost = (model.frame_rate || 0) + (model.sewing_rate || 0) + (model.upholstery_rate || 0);
    const materialCost = model.material_cost || 0;
    return workCost + materialCost;
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>
        <Typography variant="h4">
          Модели диванов
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Добавить модель
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Всего моделей
              </Typography>
              <Typography variant="h4">
                {models.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Средняя себестоимость
              </Typography>
              <Typography variant="h4">
                {models.length > 0
                  ? Math.round(models.reduce((sum, m) => sum + calculateTotalCost(m), 0) / models.length)
                  : 0} ₸
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Средняя стоимость работы
              </Typography>
              <Typography variant="h4">
                {models.length > 0
                  ? Math.round(models.reduce((sum, m) => sum + (m.frame_rate || 0) + (m.sewing_rate || 0) + (m.upholstery_rate || 0), 0) / models.length)
                  : 0} ₸
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {reorderError && <Alert severity="error" onClose={() => setReorderError('')} sx={{ mb: 2 }}>{reorderError}</Alert>}

      {(() => {
        const renderModelTable = (categoryModels, cat) => (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }}></TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Ткань 1</TableCell>
                  <TableCell>Ткань 2</TableCell>
                  <TableCell>Каркас</TableCell>
                  <TableCell>Пошив</TableCell>
                  <TableCell>Обстрелка</TableCell>
                  <TableCell>Материалы</TableCell>
                  <TableCell>Себестоимость</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <Droppable droppableId={`category-${cat}`} type={`MODELS-${cat}`} isDropDisabled={isReordering}>
                {(provided) => (
                  <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                    {categoryModels.map((model, index) => (
                      <Draggable key={String(model.id)} draggableId={`model-${model.id}`} index={index} isDragDisabled={isReordering}>
                        {(provided, snapshot) => (
                          <TableRow ref={provided.innerRef} {...provided.draggableProps} hover
                            sx={snapshot.isDragging ? { bgcolor: 'action.hover', boxShadow: 4, '& td': { bgcolor: 'background.paper' } } : {}}>
                            <TableCell sx={{ width: 40, cursor: 'grab' }} {...provided.dragHandleProps}>
                              <DragIcon color="action" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">{model.name}</Typography>
                              {model.description && <Typography variant="caption" color="text.secondary">{model.description}</Typography>}
                            </TableCell>
                            <TableCell>{model.fabric_consumption || '-'}</TableCell>
                            <TableCell>{model.fabric_consumption_2 || '-'}</TableCell>
                            <TableCell>{model.frame_rate || '-'}</TableCell>
                            <TableCell>{model.sewing_rate || '-'}</TableCell>
                            <TableCell>{model.upholstery_rate || '-'}</TableCell>
                            <TableCell>{model.material_cost || '-'}</TableCell>
                            <TableCell><Chip label={Math.round(calculateTotalCost(model))} color="primary" size="small" /></TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => handleOpenEditDialog(model)}><EditIcon fontSize="small" /></IconButton>
                                 {canDelete && <IconButton size="small" onClick={() => handleDelete(model.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </TableContainer>
        );

        return (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="model-categories" type="MODEL_CATEGORY">
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps}>
                  {categories.map((cat, index) => (
                    <Draggable key={cat} draggableId={`folder-${cat}`} index={index}>
                      {(dragProvided, snapshot) => (
                        <Box ref={dragProvided.innerRef} {...dragProvided.draggableProps}
                          sx={snapshot.isDragging ? { bgcolor: 'background.paper', boxShadow: 6 } : {}}>
                          <Accordion
                            expanded={folderPreferences.expanded[cat] !== false}
                            onChange={(_, expanded) => saveFolderPreferences({ expanded: { ...folderPreferences.expanded, [cat]: expanded } })}
                            sx={{ mb: 1 }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-label={`Папка ${cat}`}>
                              <Box display="flex" alignItems="center" width="100%" gap={1.5}>
                                <Box {...dragProvided.dragHandleProps} aria-label={`Перетащить папку ${cat}`}
                                  onClick={event => event.stopPropagation()}
                                  onKeyDown={event => event.stopPropagation()}
                                  sx={{ cursor: 'grab', display: 'flex', color: 'text.secondary', touchAction: 'none' }}>
                                  <DragIcon fontSize="small" />
                                </Box>
                                <Avatar sx={{ bgcolor: '#1e40af', width: 32, height: 32 }}>
                                  <FolderIcon fontSize="small" />
                                </Avatar>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>{cat}</Typography>
                                <Chip label={groupedModels.get(cat).length} size="small" color="primary" variant="outlined" />
                                <Box display="flex" onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
                                  <Tooltip title="Переместить папку вверх">
                                    <span><IconButton size="small" aria-label={`Переместить папку ${cat} вверх`}
                                      disabled={index === 0} onClick={() => moveFolder(index, index - 1)}>
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton></span>
                                  </Tooltip>
                                  <Tooltip title="Переместить папку вниз">
                                    <span><IconButton size="small" aria-label={`Переместить папку ${cat} вниз`}
                                      disabled={index === categories.length - 1} onClick={() => moveFolder(index, index + 1)}>
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton></span>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              {renderModelTable(groupedModels.get(cat), cat)}
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        );
      })()}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Редактировать модель' : 'Добавить модель'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Название модели"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Категория (папка)"
              value={formData.category}
              onChange={(e) => handleFormChange('category', e.target.value)}
              placeholder="Например: Тахты, Г-образные, Прямые..."
              helperText="Модели группируются по категориям"
              fullWidth
            />
            <TextField
              label="Описание"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
            <Typography variant="h6" sx={{ mt: 2 }}>Производственные данные</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Расход ткани 1 (метры)"
                  type="number"
                  value={formData.fabric_consumption}
                  onChange={(e) => handleFormChange('fabric_consumption', e.target.value)}
                  helperText="Основная ткань"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Расход ткани 2 (метры)"
                  type="number"
                  value={formData.fabric_consumption_2}
                  onChange={(e) => handleFormChange('fabric_consumption_2', e.target.value)}
                  helperText="Вторая ткань (если используется)"
                  fullWidth
                />
              </Grid>
            </Grid>
            <Typography variant="h6" sx={{ mt: 2 }}>Ставки работников (₸)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Каркасник"
                  type="number"
                  value={formData.frame_rate}
                  onChange={(e) => handleFormChange('frame_rate', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Швея"
                  type="number"
                  value={formData.sewing_rate}
                  onChange={(e) => handleFormChange('sewing_rate', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Обстрельщик"
                  type="number"
                  value={formData.upholstery_rate}
                  onChange={(e) => handleFormChange('upholstery_rate', e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Typography variant="h6" sx={{ mt: 2 }}>Себестоимость</Typography>
            <TextField
              label="Стоимость материалов (₸)"
              type="number"
              value={formData.material_cost}
              onChange={(e) => handleFormChange('material_cost', e.target.value)}
              helperText="Стоимость механизмов, пружин, поролона и т.д."
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
